import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";
import { PrintHeader } from "@/components/PrintHeader";
import { FirmaDialog } from "@/components/FirmaDialog";
import { FirmaImg } from "@/components/FirmaCliente";
import { CondividiButton } from "@/components/CondividiButton";

const Box = ({ on }) => <span className="inline-flex h-3.5 w-3.5 items-center justify-center border border-slate-700 text-[10px] font-bold leading-none">{on ? "X" : ""}</span>;

const Scelta = ({ firmata, marketing }) => (
  <div className="flex gap-6 pl-5 text-[11px]" data-testid="privacy-scelta-marketing">
    <span className="flex items-center gap-1.5"><Box on={firmata && marketing} /> Do il consenso</span>
    <span className="flex items-center gap-1.5"><Box on={firmata && !marketing} /> Nego il consenso</span>
  </div>
);

const SceltaDigitale = ({ marketing, setMarketing }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
    <p className="mb-2 font-semibold">Comunicazioni promozionali e promemoria di manutenzione (facoltativo)</p>
    <div className="grid grid-cols-2 gap-2">
      {[[true, "Do il consenso"], [false, "Nego il consenso"]].map(([v, l]) => (
        <button key={l} type="button" onClick={() => setMarketing(v)} data-testid={`firma-marketing-${v ? "si" : "no"}`}
          className={`rounded-md border px-3 py-2 font-semibold transition-colors ${marketing === v ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
      ))}
    </div>
    <p className="mt-2 text-xs text-slate-500">Firmando dichiari di aver ricevuto e letto l'informativa sul trattamento dei dati personali.</p>
  </div>
);

export default function PrivacyStampa({ pubblico }) {
  const params = useParams();
  const id = pubblico?.doc.id || params.id;
  const navigate = useNavigate();
  const [c, setC] = useState(pubblico?.doc || null);
  const [neg, setNeg] = useState(pubblico?.neg || {});
  const [open, setOpen] = useState(false);
  const [marketing, setMarketing] = useState(!!pubblico?.doc.consenso_marketing);

  const load = () => api.get(`/clienti/${id}`).then((r) => { setC(r.data); setMarketing(!!r.data.consenso_marketing); });
  useEffect(() => {
    if (pubblico) return;
    load();
    api.get("/impostazioni").then((r) => setNeg(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const salva = async (firma) => {
    try {
      await api.post(`/clienti/${id}/privacy`, { firmata: true, marketing, firma });
      toast.success("Modulo privacy firmato");
      load();
    } catch (e) {
      toast.error(formatError(e));
      throw e;
    }
  };

  if (!c) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap justify-between gap-2 px-4 print:hidden">
        {!pubblico && <Button variant="outline" onClick={() => navigate(`/clienti/${id}`)} data-testid="privacy-back-button"><ArrowLeft className="mr-1 h-4 w-4" /> Torna al cliente</Button>}
        <div className="ml-auto flex flex-wrap gap-2">
          {!pubblico && <CondividiButton tipo="privacy" id={id} telefono={c.telefono} nome={c.nome} firmato={!!c.privacy_firma} negozio={neg.nome_negozio} />}
          {!pubblico && <Button variant="outline" onClick={() => setOpen(true)} data-testid="privacy-firma-button"><PenLine className="mr-1 h-4 w-4" /> {c.privacy_firma ? "Rifai firma" : "Firma su schermo"}</Button>}
          <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="privacy-print-button"><Printer className="mr-1 h-4 w-4" /> {pubblico ? "Stampa / Salva PDF" : "Stampa"}</Button>
        </div>
      </div>
      <div className="print-sheet mx-auto max-w-[210mm] bg-white p-10 text-[12px] text-slate-900 shadow-md print:max-w-none print:p-0 print:shadow-none" data-testid="privacy-sheet">
        <PrintHeader neg={neg} overline="Titolare del trattamento" titolo="Reg. UE 2016/679 (GDPR)">
          <p className="font-heading text-base font-extrabold">{neg.titolo_privacy || "Informativa sul trattamento dei dati personali"}</p>
          <p className="text-[11px] text-slate-500">e modulo di consenso</p>
        </PrintHeader>
        <div className="mt-4 rounded border border-slate-300 p-3" data-testid="privacy-cliente">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Interessato</p>
          <p className="font-bold">{c.nome} {c.cognome}</p>
          <p>{[c.indirizzo, c.telefono, c.email].filter(Boolean).join(" · ")}</p>
          {c.codice_fiscale && <p className="font-mono">CF/P.IVA: {c.codice_fiscale}</p>}
        </div>
        <p className="mt-4 whitespace-pre-wrap text-[11px] leading-relaxed text-slate-700" data-testid="privacy-testo">{neg.informativa_privacy}</p>
        <div className="mt-6 space-y-4 rounded border border-slate-300 p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dichiarazione e consensi</p>
          <p className="text-[11px]">Il/la sottoscritto/a dichiara di aver ricevuto e letto l'informativa sopra riportata e di averne compreso il contenuto.</p>
          <div>
            <p className="mb-1.5 text-[11px] font-semibold">Comunicazioni promozionali e promemoria di manutenzione periodica (finalità c, facoltativo) via WhatsApp, SMS o email:</p>
            <Scelta firmata={!!c.privacy_firma} marketing={!!c.consenso_marketing} />
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 items-end gap-16 text-[11px] text-slate-600">
          <p>Luogo e data: ______________________ , ____ / ____ / ________</p>
          <div className="text-slate-500">
            <FirmaImg firma={c.privacy_firma} data={c.privacy_firma ? c.privacy_data : ""} testId="privacy-firma-img" />
            <div className="border-t border-slate-400 pt-1">Firma dell'interessato</div>
          </div>
        </div>
      </div>
      <FirmaDialog open={open} onOpenChange={setOpen} title="Firma modulo privacy" onSave={salva}>
        <SceltaDigitale marketing={marketing} setMarketing={setMarketing} />
      </FirmaDialog>
    </div>
  );
}
